import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { analyzeSelfie } from "@/lib/analyze.functions";
import { getUsage } from "@/lib/usage.functions";
import { generateReportPdf } from "@/lib/report.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { Header } from "@/components/Header";
import { TipItem } from "@/components/TipItem";
import { GdprConsentModal, getStoredConsent, type GdprConsent } from "@/components/GdprConsentModal";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useT, useDocumentTitleKey, useLocale } from "@/i18n/locale-context";
import { prepareImage, formatBytesShort, type PrepareProgress } from "@/lib/image-prepare";
import { useJurisdiction } from "@/hooks/useJurisdiction";
import { suggestedProtocolForGoal } from "@/lib/feature-flags";
import { PROTOCOLS } from "@/lib/protocols/definitions";
import { NotesAndActionsField, type UserAction } from "@/components/NotesAndActionsField";

export const Route = createFileRoute("/app")({
  head: () => ({
    meta: [{ title: "Mon analyse IA — Glow Up Coach" }],
  }),
  component: AppPage,
});

const GOAL_IDS = ["skin", "weight", "muscle", "antiaging"] as const;
type Goal = (typeof GOAL_IDS)[number];

type Result = {
  summary: string;
  metrics: { label: string; score: number; delta: string }[];
  tips: { title: string; detail: string; category?: string; horizon?: string; frequency?: string; disclaimer?: string }[];
};

function AppPage() {
  useDocumentTitleKey("app.head.title");
  const t = useT();
  const { locale } = useLocale();
  const { jurisdiction } = useJurisdiction();
  const nav = useNavigate();
  const analyze = useServerFn(analyzeSelfie);
  const usage = useServerFn(getUsage);
  const generateReport = useServerFn(generateReportPdf);
  const [downloading, setDownloading] = useState(false);
  const [goal, setGoal] = useState<Goal>("skin");
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [authed, setAuthed] = useState(false);
  const [userNotes, setUserNotes] = useState("");
  const [userActions, setUserActions] = useState<UserAction[]>([]);
  const [consent, setConsent] = useState<GdprConsent | null>(null);
  const [showConsent, setShowConsent] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ phase: PrepareProgress["phase"] | "sending"; percent: number } | null>(null);

  async function downloadReport() {
    setDownloading(true);
    try {
      const { base64, filename } = await generateReport({ data: { locale, jurisdiction } });
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t("app.toast.report_error"));
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => { setConsent(getStoredConsent()); }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav({ to: "/login" });
      else setAuthed(true);
    });
  }, [nav]);

  const { data: usageData, refetch: refetchUsage } = useQuery({
    queryKey: ["usage", getStripeEnvironment()],
    queryFn: () => usage({ data: { environment: getStripeEnvironment() } }),
    enabled: authed,
  });

  async function onFile(file: File) {
    if (file.size > 45 * 1024 * 1024) {
      toast.error(t("app.toast.image_too_large"));
      return;
    }
    const current = consent ?? getStoredConsent();
    if (!current) {
      setPendingFile(file);
      setShowConsent(true);
      return;
    }
    setResult(null);
    setLoading(true);
    setUploadProgress({ phase: "reading", percent: 0 });
    try {
      const prepared = await prepareImage(file, undefined, (p) => setUploadProgress(p));
      setPreview(prepared.dataUrl);
      if (prepared.recompressed && prepared.compressedBytes < prepared.originalBytes) {
        toast.success(
          t(
            "app.toast.compressed",
            formatBytesShort(prepared.originalBytes),
            formatBytesShort(prepared.compressedBytes),
          ),
        );
      }
      setUploadProgress({ phase: "sending", percent: 100 });
      const res = (await analyze({
        data: {
          imageBase64: prepared.dataUrl,
          goal,
          environment: getStripeEnvironment(),
          storePhotos: !!(consent ?? getStoredConsent())?.storePhotos,
          locale,
          jurisdiction,
          userNotes: userNotes.trim() || undefined,
          userActions: userActions.length > 0 ? userActions : undefined,
        },
      })) as Result;
      setResult(res);
      refetchUsage();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t("app.toast.analyze_error"));
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  }

  return (
    <div className="min-h-screen bg-background font-sans text-foreground" style={{ fontFamily: "var(--font-sans)" }}>
      {showConsent && (
        <GdprConsentModal
          onCancel={() => { setShowConsent(false); setPendingFile(null); }}
          onAccept={(c) => {
            setConsent(c);
            setShowConsent(false);
            const f = pendingFile;
            setPendingFile(null);
            if (f) onFile(f);
          }}
        />
      )}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap" rel="stylesheet" />
      <Header />

      <section className="max-w-5xl mx-auto px-6 py-16">
        {usageData && (
          <div className={`mb-8 p-4 rounded-2xl border flex items-center justify-between gap-4 ${usageData.isPremium ? "bg-accent/5 border-accent/30" : "bg-muted border-foreground/10"}`}>
            <p className="text-sm">
              <span className="font-bold">{usageData.isPremium ? t("app.usage.premium") : t("app.usage.free")}</span>
              <span className="text-muted-foreground ml-2">
                · {usageData.isPremium
                  ? t("app.usage.this_month_premium", usageData.analysesThisMonth)
                  : t("app.usage.this_month_free", usageData.analysesThisMonth, usageData.freeLimit)}
              </span>
            </p>
            {!usageData.isPremium && (
              <Link to="/checkout" className="bg-foreground text-background px-4 py-2 rounded-full text-xs font-bold hover:opacity-90">
                {t("app.usage.upgrade")}
              </Link>
            )}
          </div>
        )}

        <div className="mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{t("app.step1")}</span>
          <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 mb-3 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>{t("app.step1.title")}</h1>
          <p className="text-muted-foreground">{t("app.step1.desc")}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
          {GOAL_IDS.map((g) => (
            <button
              key={g}
              onClick={() => setGoal(g)}
              className={`text-left p-5 rounded-2xl border transition-all ${goal === g ? "border-accent bg-accent/5 ring-1 ring-accent" : "border-foreground/10 hover:border-foreground/30"}`}
            >
              <p className="font-bold text-sm mb-1">{t(`app.goal.${g}`)}</p>
              <p className="text-xs text-muted-foreground">{t(`app.goal.${g}.desc`)}</p>
            </button>
          ))}
        </div>

        <div className="mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{t("app.step2")}</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 mb-3 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>{t("app.step2.title")}</h2>
          <p className="text-muted-foreground text-sm">{t("app.step2.desc")}</p>
        </div>

        <div className="mb-8">
          <NotesAndActionsField
            notes={userNotes}
            onNotesChange={setUserNotes}
            actions={userActions}
            onActionsChange={setUserActions}
            disabled={loading}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <label className="aspect-[4/5] rounded-3xl border-2 border-dashed border-foreground/15 hover:border-accent/60 hover:bg-accent/5 transition-all cursor-pointer flex flex-col items-center justify-center text-center p-8 relative overflow-hidden">
            {preview ? (
              <img src={preview} alt={t("app.upload.alt")} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <>
                <div className="size-14 rounded-full bg-accent/10 grid place-items-center mb-4">
                  <div className="size-6 rounded-full border-2 border-accent" />
                </div>
                <p className="font-semibold mb-1">{t("app.upload.prompt")}</p>
                <p className="text-xs text-muted-foreground">{t("app.upload.specs")}</p>
              </>
            )}
            <input type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
          </label>

          <div className="bg-primary text-primary-foreground rounded-3xl p-8 min-h-[400px] flex flex-col">
            {loading && (
              <div className="m-auto text-center w-full max-w-xs">
                <div className="size-3 bg-accent rounded-full animate-pulse mx-auto mb-4" />
                <p className="text-sm text-primary-foreground/70 mb-3">
                  {uploadProgress
                    ? t(`app.upload.phase.${uploadProgress.phase}`)
                    : t("app.result.loading")}
                </p>
                <div className="h-1.5 w-full bg-primary-foreground/15 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-accent transition-all duration-200 ${
                      uploadProgress?.phase === "sending" ? "animate-pulse" : ""
                    }`}
                    style={{ width: `${uploadProgress?.percent ?? 100}%` }}
                  />
                </div>
                {uploadProgress && (
                  <p className="text-[10px] text-primary-foreground/50 mt-2 tabular-nums">
                    {Math.round(uploadProgress.percent)}%
                  </p>
                )}
              </div>
            )}
            {!loading && !result && (
              <div className="m-auto text-center text-primary-foreground/50 text-sm">{t("app.result.placeholder")}</div>
            )}
            {result && (
              <>
                <h3 className="font-display font-bold text-xl mb-2" style={{ fontFamily: "var(--font-display)" }}>{t("app.result.title")}</h3>
                <p className="text-sm text-primary-foreground/70 mb-6">{result.summary}</p>

                {result.metrics?.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {result.metrics.map((m) => (
                      <div key={m.label}>
                        <div className="flex justify-between text-[11px] font-bold uppercase tracking-wider mb-1">
                          <span>{m.label}</span>
                          <span className="text-accent">{m.delta}</span>
                        </div>
                        <div className="h-1.5 w-full bg-primary-foreground/10 rounded-full overflow-hidden">
                          <div className="h-full bg-accent" style={{ width: `${m.score}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <ul className="space-y-5 mt-auto">
                  {result.tips?.map((tip, i) => (
                    <TipItem
                      key={i}
                      index={i}
                      tip={tip}
                      numberClassName="text-accent font-display text-2xl font-bold leading-none"
                      detailClassName="text-primary-foreground/60 text-xs leading-relaxed"
                      badgeClassName="text-[10px] uppercase tracking-wider bg-primary-foreground/10 text-primary-foreground/80 px-2 py-0.5 rounded-full"
                      disclaimerClassName="text-[11px] text-accent/90 mt-2 italic"
                    />
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        <div className="mt-12 text-center">
          {result && (
            <div className="mb-6">
              <button onClick={downloadReport} disabled={downloading} className="inline-flex items-center gap-2 bg-foreground text-background px-6 py-3 rounded-full text-sm font-bold hover:opacity-90 disabled:opacity-50">
                {downloading ? t("app.report.generating") : t("app.report.download")}
              </button>
              <p className="text-xs text-muted-foreground mt-3">{t("app.report.subline")}</p>
            </div>
          )}
          {result && (() => {
            const suggested = suggestedProtocolForGoal(goal);
            const def = PROTOCOLS.find((p) => p.goal === suggested);
            if (!def) return null;
            return (
              <div className="mb-8 mx-auto max-w-2xl rounded-2xl border border-accent/30 bg-accent/5 p-6 text-left">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent mb-2">
                  {t("app.protocol.cta.kicker")}
                </p>
                <h3 className="font-display font-bold text-xl mb-1" style={{ fontFamily: "var(--font-display)" }}>
                  {t("app.protocol.cta.title", def.title[locale])}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">{def.subtitle[locale]}</p>
                <Link
                  to="/protocols/$goal"
                  params={{ goal: suggested }}
                  className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-5 py-2.5 rounded-full text-sm font-bold hover:opacity-90"
                >
                  {t("app.protocol.cta.button")}
                </Link>
              </div>
            );
          })()}
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">{t("app.back_home")}</Link>
        </div>
      </section>
    </div>
  );
}
